import { Banknote, Coins, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import type { CashCountItem } from "@/hooks/useCashCount";
import type { PaymentMethod } from "@/hooks/usePaymentMethods";

interface PaymentMethodCount {
  paymentMethodId: string;
  paymentMethodName: string;
  amount: number;
}

interface CashCountFormProps {
  notes: CashCountItem[];
  coins: CashCountItem[];
  total: number;
  onUpdateQuantity: (denomination: number, quantity: number) => void;
  // Novos props para contagem de comprovantes
  paymentMethods?: PaymentMethod[];
  paymentMethodCounts?: PaymentMethodCount[];
  onUpdatePaymentMethodAmount?: (paymentMethodId: string, amount: number) => void;
}

export function CashCountForm({
  notes,
  coins,
  total,
  onUpdateQuantity,
  paymentMethods = [],
  paymentMethodCounts = [],
  onUpdatePaymentMethodAmount,
}: CashCountFormProps) {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  // Filtrar métodos de pagamento ativos que NÃO são dinheiro
  const nonCashPaymentMethods = paymentMethods.filter(
    (pm) => pm.is_active && pm.type !== "cash"
  );

  const getPaymentMethodAmount = (paymentMethodId: string) => {
    const found = paymentMethodCounts.find(
      (pmc) => pmc.paymentMethodId === paymentMethodId
    );
    return found?.amount || 0;
  };

  const totalPaymentMethods = paymentMethodCounts.reduce(
    (sum, pmc) => sum + pmc.amount,
    0
  );

  return (
    <div className="space-y-6">
      {/* Notes */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Banknote className="h-5 w-5 text-primary" />
          <Label className="text-base font-medium">Notas</Label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {notes.map((item) => (
            <div
              key={item.denomination}
              className="flex items-center gap-2 p-3 border rounded-lg"
            >
              <span className="text-sm font-medium w-16">
                {formatCurrency(item.denomination)}
              </span>
              <Input
                type="number"
                min="0"
                value={item.quantity || ""}
                onChange={(e) =>
                  onUpdateQuantity(item.denomination, parseInt(e.target.value) || 0)
                }
                className="w-20 text-center"
                placeholder="0"
              />
              <span className="text-sm text-muted-foreground flex-1 text-right">
                = {formatCurrency(item.total)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Coins */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Coins className="h-5 w-5 text-primary" />
          <Label className="text-base font-medium">Moedas</Label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {coins.map((item) => (
            <div
              key={item.denomination}
              className="flex items-center gap-2 p-3 border rounded-lg"
            >
              <span className="text-sm font-medium w-16">
                {formatCurrency(item.denomination)}
              </span>
              <Input
                type="number"
                min="0"
                value={item.quantity || ""}
                onChange={(e) =>
                  onUpdateQuantity(item.denomination, parseInt(e.target.value) || 0)
                }
                className="w-20 text-center"
                placeholder="0"
              />
              <span className="text-sm text-muted-foreground flex-1 text-right">
                = {formatCurrency(item.total)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Cash Total */}
      <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
        <span className="text-lg font-medium">Total em Dinheiro</span>
        <span className="text-2xl font-bold text-primary">
          {formatCurrency(total)}
        </span>
      </div>

      {/* Payment Methods Section */}
      {nonCashPaymentMethods.length > 0 && onUpdatePaymentMethodAmount && (
        <>
          <Separator />
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="h-5 w-5 text-primary" />
              <Label className="text-base font-medium">Comprovantes de Pagamento</Label>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Informe o valor total dos comprovantes de cada forma de pagamento.
            </p>
            <div className="grid grid-cols-1 gap-3">
              {nonCashPaymentMethods.map((pm) => (
                <div
                  key={pm.id}
                  className="flex items-center gap-3 p-3 border rounded-lg"
                >
                  <span className="text-sm font-medium flex-1 min-w-0 truncate">
                    {pm.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">R$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={getPaymentMethodAmount(pm.id) || ""}
                      onChange={(e) =>
                        onUpdatePaymentMethodAmount(pm.id, parseFloat(e.target.value) || 0)
                      }
                      className="w-28 text-right"
                      placeholder="0,00"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Payment Methods Total */}
          <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
            <span className="text-base font-medium">Total Comprovantes</span>
            <span className="text-xl font-bold">
              {formatCurrency(totalPaymentMethods)}
            </span>
          </div>
        </>
      )}

      {/* Grand Total */}
      {nonCashPaymentMethods.length > 0 && onUpdatePaymentMethodAmount && (
        <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg border-2 border-primary/20">
          <span className="text-lg font-bold">Total Geral</span>
          <span className="text-2xl font-bold text-primary">
            {formatCurrency(total + totalPaymentMethods)}
          </span>
        </div>
      )}
    </div>
  );
}
